import React from 'react';
import {
    Box,
    ButtonGroup,
    CircularProgress,
    Divider,
    Grid,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {Check as CheckIcon, Close as CloseIcon} from '@mui/icons-material';
import dayjs from 'dayjs';
import {OffeneAntraegeTabProps} from '../../../../Contexts/Urlaubsantrag/UrlaubsantragContext';

export const OffeneAntraegeTab: React.FC<OffeneAntraegeTabProps> = ({
                                                                        loading,
                                                                        antraege,
                                                                        onProcess,
                                                                    }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // Format date for display
    const formatDate = (dateString: string) => {
        return dayjs(dateString).format('DD.MM.YYYY');
    };

    if (loading) {
        return (
            <Box sx={{display: 'flex', justifyContent: 'center', p: 3}}>
                <CircularProgress/>
            </Box>
        );
    }

    if (antraege.length === 0) {
        return (
            <Box sx={{p: 2}}>
                <Typography variant='body1'>Keine offenen Urlaubsanträge vorhanden</Typography>
            </Box>
        );
    }

    // Mobile layout with cards
    if (isMobile) {
        return (
            <Box sx={{p: 1}}>
                {antraege.map((antrag) => (
                    <Paper
                        key={antrag.id}
                        elevation={2}
                        sx={{
                            mb: 2,
                            overflow: 'hidden',
                            borderRadius: 1,
                        }}>
                        <Box
                            sx={{
                                p: 2,
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                bgcolor: 'rgba(0, 0, 0, 0.03)',
                            }}>
                            <Box>
                                <Typography variant='subtitle1' fontWeight='medium'>
                                    {antrag.username}
                                </Typography>
                                <Typography variant='body2' color='text.secondary'>
                                    {formatDate(antrag.beginn)} - {formatDate(antrag.ende)}
                                </Typography>
                            </Box>
                        </Box>
                        <Divider/>
                        <Box sx={{p: 2}}>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Typography variant='body2' color='text.secondary'>
                                        Grund:
                                    </Typography>
                                    <Typography variant='body1'>{antrag.grund || '-'}</Typography>
                                </Grid>
                                {antrag?.notiz && (
                                    <Grid item xs={12}>
                                        <Typography variant='body2' color='text.secondary'>
                                            Notizen:
                                        </Typography>
                                        <Typography variant='body1'>{antrag.notiz || '-'}</Typography>
                                    </Grid>
                                )}
                                <Grid item xs={12}>
                                    <Typography variant='body2' color='text.secondary'>
                                        Eingereicht am:
                                    </Typography>
                                    <Typography variant='body1'>{formatDate(antrag.antrag_datum)}</Typography>
                                </Grid>
                                <Grid item xs={12} sx={{mt: 1}}>
                                    <ButtonGroup variant='contained' size='small' fullWidth>
                                        <IconButton
                                            color='success'
                                            onClick={() => onProcess(antrag.id, 'Genehmigt')}
                                            title='Genehmigen'
                                            sx={{borderRadius: 0, py: 1, flexGrow: 1}}>
                                            <CheckIcon/>
                                            <Typography
                                                variant='button'
                                                sx={{ml: 1, display: {xs: 'none', sm: 'inline'}}}>
                                                Genehmigen
                                            </Typography>
                                        </IconButton>
                                        <IconButton
                                            color='error'
                                            onClick={() => onProcess(antrag.id, 'Abgelehnt')}
                                            title='Ablehnen'
                                            sx={{borderRadius: 0, py: 1, flexGrow: 1}}>
                                            <CloseIcon/>
                                            <Typography
                                                variant='button'
                                                sx={{ml: 1, display: {xs: 'none', sm: 'inline'}}}>
                                                Ablehnen
                                            </Typography>
                                        </IconButton>
                                    </ButtonGroup>
                                </Grid>
                            </Grid>
                        </Box>
                    </Paper>
                ))}
            </Box>
        );
    }

    // Desktop layout with table
    return (
        <TableContainer sx={{overflowX: 'auto'}}>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{fontWeight: 'bold'}}>Mitarbeiter</TableCell>
                        <TableCell sx={{fontWeight: 'bold'}}>Von</TableCell>
                        <TableCell sx={{fontWeight: 'bold'}}>Bis</TableCell>
                        <TableCell sx={{fontWeight: 'bold'}}>Grund</TableCell>
                        <TableCell sx={{fontWeight: 'bold'}}>Notizen</TableCell>
                        <TableCell sx={{fontWeight: 'bold'}}>Urlaubstage</TableCell>
                        <TableCell sx={{fontWeight: 'bold'}}>Eingereicht am</TableCell>
                        <TableCell sx={{fontWeight: 'bold'}}>Aktionen</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {antraege.map((antrag) => (
                        <TableRow key={antrag.id}>
                            <TableCell>{antrag.username}</TableCell>
                            <TableCell>{formatDate(antrag.beginn)}</TableCell>
                            <TableCell>{formatDate(antrag.ende)}</TableCell>
                            <TableCell>{antrag.grund || '-'}</TableCell>
                            <TableCell>{antrag?.notiz || '-'}</TableCell>
                            <TableCell>{antrag?.working_days || '-'}</TableCell>
                            <TableCell>{formatDate(antrag.antrag_datum)}</TableCell>
                            <TableCell>
                                <IconButton
                                    size='small'
                                    color='success'
                                    onClick={() => onProcess(antrag.id, 'Genehmigt')}
                                    title='Genehmigen'>
                                    <CheckIcon/>
                                </IconButton>
                                <IconButton
                                    size='small'
                                    color='error'
                                    onClick={() => onProcess(antrag.id, 'Abgelehnt')}
                                    title='Ablehnen'>
                                    <CloseIcon/>
                                </IconButton>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};
